import { isBadSendOutcome, sendStateLabel, type InvoiceSendState } from '@jobos/shared';
import { cn } from '@/lib/utils';

/**
 * The bounce badge (FR-4.6) — W4's headline acceptance criterion: a failed
 * send is visible on the invoice list without anyone opening an inbox.
 *
 * It renders for BAD outcomes only. A `delivered` send gets nothing, and the
 * absence of a red badge is the reassurance; a green "Delivered" chip on every
 * row would bury the handful that matter.
 *
 * Two rules live in `@jobos/shared` and are NOT restated here:
 *
 *  - which states are bad — `isBadSendOutcome`, shared with the backend;
 *  - what each one is called — `sendStateLabel`.
 *
 * Re-deriving either in a component is how the list and the send dialog come
 * to disagree about the same invoice.
 */

/*
 * This badge calls `sendStateLabel`, which takes only a state — NOT
 * `sendOutcomeLabel`, which needs a `createdAt` the row feeds do not carry
 * (they have `lastSendState` and `lastSendDetail`, no send timestamp).
 *
 * An earlier version passed an inert epoch to satisfy that parameter. It was
 * correct, but only by luck: the two age-dependent states are `pending` and
 * `accepted`, neither of which is a bad outcome, so this badge never reached
 * those branches. The day a bad state grows an age-dependent label, the inert
 * clock would have started quietly lying — and the test looping over
 * SEND_STATES would not have caught it, since it uses the same inert clock.
 * Splitting the function moves that from a comment to the type system.
 */

export interface SendOutcomeBadgeProps {
  /** The newest send's state. Null/undefined = never sent, or a legacy row. */
  state?: InvoiceSendState | null;
  /**
   * The provider's text for the failure. Safe to show HERE precisely because
   * this badge only renders when `isBadSendOutcome(state)` — a transient
   * bounce writes "mailbox full" and the later `delivered` cannot clear it, so
   * this string must never be lifted onto anything that renders in other
   * states (spec D5).
   */
  detail?: string | null;
  className?: string;
}

export function SendOutcomeBadge({ state, detail, className }: SendOutcomeBadgeProps) {
  if (!state || !isBadSendOutcome(state)) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5',
        'bg-red-100 text-xs font-medium text-red-800',
        className,
      )}
      title={detail ?? undefined}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {sendStateLabel(state)}
    </span>
  );
}
